/**
 * Las clases de los controles que se repiten en toda la app.
 *
 * Nacieron en el onboarding (`onboarding/PantallaPregunta.tsx`, que las sigue
 * reexportando para no tocar sus importadores) y se mudaron aquí cuando el
 * resto de la plataforma empezó a usarlas: un botón primario es el mismo en la
 * pregunta del onboarding que en la sección de ajustes o en la ficha de admin.
 *
 * Son cadenas y no componentes a propósito. Un `<Link>`, un `<button>` y un
 * `<a download>` llevan el mismo aspecto sin tener que envolver cada uno en un
 * componente que reenvíe props; se compone con `[ESTILO, extra].join(" ")`.
 *
 * Ningún color literal: solo tokens de `globals.css` y la gama `zinc`/`red`
 * que el estándar deja fuera de los tokens.
 */

export const BOTON_PRIMARIO =
  "inline-flex min-h-12 items-center justify-center rounded-xl bg-brand px-6 text-sm font-semibold text-white transition hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";
export const BOTON_SECUNDARIO =
  "inline-flex min-h-12 items-center justify-center rounded-xl border border-line-strong bg-white px-5 text-sm font-medium text-zinc-700 transition hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus disabled:opacity-60";
export const BOTON_ENLACE =
  "text-sm font-medium text-zinc-500 underline-offset-4 hover:text-zinc-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus rounded";
export const CAMPO =
  "w-full rounded-xl border border-line-strong bg-white px-4 py-3 text-base text-zinc-900 placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus disabled:opacity-60";

/** El campo de una sola cifra: grande y centrado, que es toda la pantalla. */
export const CAMPO_NUMERO =
  "w-full rounded-2xl border border-line-strong bg-white px-4 py-5 text-center text-4xl font-semibold tabular-nums text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus disabled:opacity-60";

/**
 * Lo que borra, revoca o expulsa. Rojo sólido: se ve que no es un botón más,
 * y en la app solo aparece detrás de un `ConfirmButton`.
 */
export const BOTON_PELIGRO =
  "inline-flex min-h-12 items-center justify-center rounded-xl bg-red-600 px-5 text-sm font-semibold text-white transition hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

/**
 * Botón redondo de solo icono (lápiz, papelera, cerrar). La forma es la del
 * botón de la campana de `PushPermissionPrompt`; el tamaño baja a 44 px, que
 * es el mínimo táctil, para caber en una fila de tabla o en una tarjeta.
 * Siempre con `aria-label`: sin texto, es lo único que lo nombra.
 */
export const BOTON_ICONO =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-surface-subtle hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus disabled:cursor-not-allowed disabled:opacity-60";

/** La superficie de una tarjeta o sección: blanca, borde de un tono, sin sombra. */
export const TARJETA = "rounded-2xl border border-line bg-white p-4 sm:p-5";

/**
 * Una fila o tarjeta que se abre entera al tocarla. Solo el aspecto: el
 * comportamiento (qué clics se ignoran, Ctrl-clic) es de `DataTable` y de
 * `RecordCard`.
 */
export const FILA_CLICABLE = "cursor-pointer transition-colors hover:bg-surface-subtle";
